import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, FileText, CalendarDays, Users, Building2, LogOut, FolderDown, Contact } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { AIChatWidget } from "../../components/AIChatWidget";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-tab-dashboard" },
  { to: "/app/intake", label: "Intake", icon: FileText, testid: "nav-tab-intake" },
  { to: "/app/appointments", label: "Appointments", icon: CalendarDays, testid: "nav-tab-appointments" },
  { to: "/app/clients", label: "Clients", icon: Contact, testid: "nav-tab-clients", staffOnly: true },
  { to: "/app/forms", label: "Documents", icon: FolderDown, testid: "nav-tab-documents", clientOnly: true },
  { to: "/app/team", label: "Team", icon: Users, testid: "nav-tab-team", adminOnly: true },
  { to: "/app/office", label: "Office", icon: Building2, testid: "nav-tab-office" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (!user || user.role === "client") return;
    api.get("/appointments/pending-count").then((r) => setPending(r.data.count)).catch(() => {});
  }, [user, location.pathname]);

  const PendingBadge = ({ item, small }) => item.to === "/app/appointments" && pending > 0 ? (
    <span data-testid={small ? "mobile-pending-count-badge" : "pending-count-badge"}
      className={`ml-auto inline-flex items-center justify-center rounded-full bg-accent text-accent-foreground font-semibold ${small ? "min-w-4 h-4 px-1 text-[10px]" : "min-w-5 h-5 px-1.5 text-[11px]"}`}>
      {pending}
    </span>
  ) : null;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row" data-testid="app-layout">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-border bg-card">
        <div className="h-16 flex items-center px-6 border-b border-border">
          <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-11 w-auto rounded-sm" />
        </div>
        <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
          {navItems.filter((i) => (!i.clientOnly || user?.role === "client") && (!i.staffOnly || user?.role !== "client") && (!i.adminOnly || user?.role === "admin")).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid} className={linkClass}>
              <item.icon className="w-4 h-4" />
              {item.label}
              <PendingBadge item={item} />
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 px-2">
            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <Badge variant="secondary" className="text-[10px] capitalize" data-testid="sidebar-user-role">{user?.role}</Badge>
            </div>
            <button onClick={handleLogout} data-testid="logout-button" title="Sign out"
              className="text-muted-foreground hover:text-destructive transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center">
              <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-9 w-auto rounded-sm" />
            </div>
            <button onClick={handleLogout} data-testid="mobile-logout-button" className="text-muted-foreground">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <nav className="flex overflow-x-auto px-2 pb-2 gap-1" data-testid="mobile-nav">
            {navItems.filter((i) => (!i.clientOnly || user?.role === "client") && (!i.staffOnly || user?.role !== "client") && (!i.adminOnly || user?.role === "admin")).map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={`mobile-${item.testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  }`
                }>
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
                <PendingBadge item={item} small />
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <AIChatWidget />
    </div>
  );
}
